import { Injectable } from '@angular/core';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConfiguratorScene } from '@3d-scene/scene/configurator-scene';
import { MaterialsModel } from '@3d-scene/service/materials.model';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { Box3, BufferAttribute, BufferGeometry, Color, EdgesGeometry, Group, LineBasicMaterial, LineSegments, Mesh, Vector3 } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { ArticleContainer } from '../../model3d/article-container';

@Injectable({ providedIn: 'root' })
export class WireContourService {
  public static readonly WIRECONTOUR_NAME = 'wirecontour' as const;
  public static readonly WIRECONTOUR_PDF_NAME = 'wirecontour_pdf' as const;
  public static readonly BRIGHTER_CONTOURS_LUMINANCE_THRESHOLD = 0.2 as const;


  constructor(
    private materialsModel: MaterialsModel,
    private configuratorScene: ConfiguratorScene,
    private containerService: ContainerService,
  ) {
  }

  public createWireContour(container: ArticleContainer) {
    let group: Group | null = container.model;

    // for wire bridges create wirecontour only for the line meshes, flags are handled with their own selectionHandler
    if (ArticleUtils.isWireBridgeArticle(container.article)) {
      group = Object3DUtils.getChildByName(container, WireBridgeService.WIRE_OBJECT_NAME) as Group;
      if (group && !group.visible) {
        group = null;
      }
    }

    if (!group) return;

    if (container.wirecontour) {
      container.remove(container.wirecontour);
    }

    const allMeshes = MeshUtils.getAllMeshesFromObject(group, true).filter(mesh => this.shouldCreateMeshWirecontour(mesh));

    const brightMeshes: Mesh[] = [];
    const darkMeshes: Mesh[] = [];

    for (const mesh of allMeshes) {
      const isDark = (mesh.material.color as Color)?.getHSL({h: 0, s: 0, l: 0}).l < WireContourService.BRIGHTER_CONTOURS_LUMINANCE_THRESHOLD;
      if (isDark) {
        darkMeshes.push(mesh);
      } else {
        brightMeshes.push(mesh);
      }
    }

    container.wirecontour = new Group();

    if (brightMeshes.length > 0) {
      this.createContourForMeshes(brightMeshes, container, group, false);
    }

    if (darkMeshes.length > 0) {
      this.createContourForMeshes(darkMeshes, container, group, true);
    }
  }

  private createContourForMeshes(meshes: Mesh[], container: ArticleContainer, group: Group, useGreyContour: boolean): void {
    const outlinedGeometry = MeshUtils.mergeMeshes(meshes);

    /* We push the vertices of the outline away from the objects center by a small amount. This way the likelihood of the contour disappearing inside
     * the geometry is lower - although the best (but more complicated way) would be to move the vertices along the average of their adjacent faces normals */
    MeshUtils.pushVerticesAwayFromCenter(outlinedGeometry, 0.0025);
    let thresholdAngle = this.getThresholdAngle(container, 30);

    const lineSegments = new LineSegmentsGeometry().fromEdgesGeometry(new EdgesGeometry(outlinedGeometry, thresholdAngle));
    const lines = new LineSegments2(lineSegments, this.getContourMaterial(container, useGreyContour));
    lines.userData.brighter = useGreyContour;
    container.wirecontour.add(lines);
    container.wirecontour.renderOrder = 10;
    container.wirecontour.name = WireContourService.WIRECONTOUR_NAME;
    container.wirecontour.visible = true;
    ArticleUtils.isWireBridgeArticle(container.article) ? group.add(container.wirecontour) : container.add(container.wirecontour);
  }

  private getContourMaterial(container: ArticleContainer, useGreyContour: boolean): LineMaterial {
    const defaultContourMaterial = useGreyContour
      ? this.materialsModel.materialContourGrey
      : this.materialsModel.materialContour;

    return ArticleUtils.isDrillingHole(container.article)
      ? this.materialsModel.materialContourDrillingHole
      : defaultContourMaterial;
  }

  private getThresholdAngle(container: ArticleContainer, fallback: number): number {
    let contourThresholdAngle = ArticleUtils.getArticleBehaviorPropertyIntValue(container.article, ArticleBehaviorPropertyDef.CONTOUR_THRESHOLD_ANGLE);
    return isNaN(contourThresholdAngle) ? fallback : contourThresholdAngle;
  }

  public redrawAllWireContours(): void {
    for(const container of this.containerService.enumerateArticleContainersInGlobal()) {
      this.createWireContour(container);
    }
    this.configuratorScene.renderScene();
  }

  public validateWireContour(container: ArticleContainer) {
    if (!container.wirecontour || container.wirecontour.children.length == 0) {
      this.createWireContour(container);
    }
  }

  public createWireContourForPdf(container: ArticleContainer) {
    let group: Group | null = container.model;
    // for wire bridges create wirecontour only for the line meshes, flags are handled with their own selectionHandler
    if (ArticleUtils.isWireBridgeArticle(container.article)) {
      group = Object3DUtils.getChildByName(container, WireBridgeService.WIRE_OBJECT_NAME) as Group;
      if (group && !group.visible) {
        group = null;
      }
    }
    if (!group) {
      return;
    }
    if (container.wirecontour) {
      container.remove(container.wirecontour);
    }

    const meshes = MeshUtils.getAllMeshesFromObject(group, true).filter(mesh => this.shouldCreateMeshWirecontour(mesh));

    if (meshes.length == 0) return;

    const outlinedGeometry = MeshUtils.mergeMeshes(meshes);

    /* We push the vertices of the outline away from the objects center by a small amount. This way the likelihood of the contour disappearing inside
     * the geometry is lower - although the best (but more complicated way) would be to move the vertices along the average of their adjacent faces normals */
    MeshUtils.pushVerticesAwayFromCenter(outlinedGeometry, 0.0025);

    const vertices: any[] = [];

    // move vertex x/y/z position on this offset
    const vertexDelta = 0.01;
    const createVectorWithVertexOffset = (x: number, y: number, z: number, middlePoint: Vector3) => {
      x = x >= middlePoint.x ? x + vertexDelta : x - vertexDelta;
      y = y >= middlePoint.y ? y + vertexDelta : y - vertexDelta;
      z = z >= middlePoint.z ? z + vertexDelta : z - vertexDelta;
      return new Vector3(x, y, z);
    };

    // increase the size wirecontour for better displaying
    const convertEdgeGeometryToVerticesArray = (geometry: BufferGeometry, array: Vector3[]) => {
      const positionAttribute = geometry.getAttribute('position') as BufferAttribute;
      const box: Box3 = new Box3().setFromArray(positionAttribute.array);
      const middlePoint: Vector3 = box.getCenter(new Vector3());
      const edgesVertices = positionAttribute.array;
      for (let v = 0; v < edgesVertices.length; v += 3) {
        array.push(createVectorWithVertexOffset(edgesVertices[v], edgesVertices[v + 1], edgesVertices[v + 2], middlePoint));
      }
    };

    if (meshes.length > 0) {
      let thresholdAngle = this.getThresholdAngle(container, 1);
      convertEdgeGeometryToVerticesArray(new EdgesGeometry(outlinedGeometry, thresholdAngle), vertices);
    }

    const geometry = new BufferGeometry();
    geometry.setFromPoints(vertices);

    const mat = new LineBasicMaterial({ color: ArticleUtils.isDrillingHole(container.article) ? 0xffffff : 0x000000, linewidth: 1 });
    const wirecontour = new LineSegments(geometry, mat);
    wirecontour.renderOrder = 10;
    wirecontour.name = WireContourService.WIRECONTOUR_PDF_NAME;
    wirecontour.visible = true;
    ArticleUtils.isWireBridgeArticle(container.article) ? group.add(wirecontour) : container.add(wirecontour);
    container.wirecontour = wirecontour;

    this.configuratorScene.renderScene();
  }

  private shouldCreateMeshWirecontour(mesh: Mesh): boolean {
    if (!mesh) {
      return false;
    }
    if (MeshUtils.isTextureElement(mesh.name)) {
      return true;
    }
    return !MeshUtils.meshGeometryIsFlat(mesh.geometry);
  }
}
